import { Component } from "@angular/core";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { ICellRendererParams } from "ag-grid-community";

/**
 * Row-level action icons for the tasks table view. Renders three small icon
 * buttons (Edit / Comments / Attachments) that open the main task dialog
 * directly on the corresponding tab.
 *
 * Communicates with the parent component (TaskManagementWfComponent) via the
 * `context.componentParent` escape hatch that ag-grid exposes on the grid
 * options. This avoids a tight coupling via @Input/@Output and works cleanly
 * inside ag-grid's dynamic cell-renderer system.
 *
 * Every button calls `event.stopPropagation()` so the parent row's click
 * handler (which opens the subtask sidebar) does not also fire — the table's
 * `onRowClicked` has a second belt-and-braces check on the column id as well.
 */
@Component({
    selector: "app-actions-cell-renderer",
    template: `
    <div class="grid-actions">
      <button
        type="button"
        class="grid-action-btn"
        (click)="onEdit($event)"
        matTooltip="Edit"
      >
        <mat-icon>edit</mat-icon>
      </button>
      <button
        type="button"
        class="grid-action-btn"
        (click)="onComments($event)"
        matTooltip="Comments"
      >
        <mat-icon>chat_bubble_outline</mat-icon>
        <span *ngIf="(params.data?._commentCount ?? params.data?._raw?.comment_count ?? params.data?.comment_count ?? 0) > 0" class="grid-action-badge">{{ params.data?._commentCount ?? params.data?._raw?.comment_count ?? params.data?.comment_count }}</span>
      </button>
      <button
        type="button"
        class="grid-action-btn"
        (click)="onAttachments($event)"
        matTooltip="Attachments"
      >
        <mat-icon>attach_file</mat-icon>
        <span *ngIf="(params.data?._attachmentCount ?? params.data?._raw?.attachment_count ?? params.data?.attachment_count ?? 0) > 0" class="grid-action-badge">{{ params.data?._attachmentCount ?? params.data?._raw?.attachment_count ?? params.data?.attachment_count }}</span>
      </button>
    </div>
  `,
    styles: [
        `
           .grid-actions {
             display: flex;
             align-items: center;
             gap: 2px;
             height: 100%;
           }
           .grid-action-btn {
             width: 26px;
             height: 26px;
             padding: 0;
             border: none;
             background: transparent;
             cursor: pointer;
             color: #a0a09c;
             border-radius: 4px;
             display: inline-flex;
             align-items: center;
             justify-content: center;
             gap: 2px;
             transition: color 0.12s, background 0.12s;
           }
           .grid-action-btn:hover {
             color: #1a1a18;
             background: rgba(0, 0, 0, 0.06);
           }
           .grid-action-btn mat-icon,
           .grid-action-btn .mat-icon {
             font-size: 16px;
             width: 16px;
             height: 16px;
             line-height: 16px;
           }
           .grid-action-badge {
             font-size: 10px;
             font-weight: 700;
             color: #6b6b67;
             min-width: 8px;
             line-height: 1;
             margin-left: -1px;
           }
         `,
    ],
    standalone: false
})
export class ActionsCellRendererComponent implements ICellRendererAngularComp {
  params!: ICellRendererParams;
  commentCount = 0;
  attachmentCount = 0;
  isSubtask = false;

  agInit(params: ICellRendererParams): void {
    this.params = params;
    const d: any = params.data || {};
    this.commentCount = d._commentCount ?? d._raw?.comment_count ?? d.comment_count ?? 0;
    this.attachmentCount = d._attachmentCount ?? d._raw?.attachment_count ?? d.attachment_count ?? 0;
    this.isSubtask = !!(d.parent_task_id || d._raw?.parent_task_id);
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    const d: any = params.data || {};
    this.commentCount = d._commentCount ?? d._raw?.comment_count ?? d.comment_count ?? 0;
    this.attachmentCount = d._attachmentCount ?? d._raw?.attachment_count ?? d.attachment_count ?? 0;
    this.isSubtask = !!(d.parent_task_id || d._raw?.parent_task_id);
    return true;
  }

  /** 0 = Task Details tab (matches the numeric `selectedType` wired in
   *  TaskDetailContentComponent and the kanban-card edit-icon flow). */
  onEdit(event: Event): void {
    event.stopPropagation();
    if (this.isSubtask) {
      this.invokeSubtaskParent('details');
    } else {
      this.invokeParent(0);
    }
  }

  /** 1 = Task Updates tab. */
  onComments(event: Event): void {
    event.stopPropagation();
    if (this.isSubtask) {
      this.invokeSubtaskParent('updates');
    } else {
      this.invokeParent(1);
    }
  }

  /** 2 = Attachments tab. */
  onAttachments(event: Event): void {
    event.stopPropagation();
    if (this.isSubtask) {
      this.invokeSubtaskParent('attachments');
    } else {
      this.invokeParent(2);
    }
  }

  /** Hand off to the parent component's shared `onEditTask` method (already
   *  wires get-by-id refresh + dialog open on the requested tab). Tolerates a
   *  missing context/parent so the grid doesn't crash if it is ever rendered
   *  outside TaskManagementWfComponent. */
  private invokeParent(initialTab: number): void {
    const parent = (this.params as any)?.context?.componentParent;
    if (parent && typeof parent.onEditTask === "function") {
      parent.onEditTask(this.params.data, initialTab);
    }
  }

  /** Hand off to the parent component's subtask-specific handler which opens
   *  the subtask sidebar + edit dialog on the requested tab. */
  private invokeSubtaskParent(tab: 'details' | 'updates' | 'attachments'): void {
    const parent = (this.params as any)?.context?.componentParent;
    if (parent && typeof parent.onSubtaskActionFromGrid === "function") {
      parent.onSubtaskActionFromGrid(this.params.data, tab);
    }
  }
}
